import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Divider,
  Grid,
  Box,
  Typography,
  Modal,
  IconButton,
} from "@mui/material";
import {
  LocationOn as MapPinIcon,
  Phone as PhoneIcon,
  AccessTime as ClockIcon,
  Email as MailIcon,
} from "@mui/icons-material";

import CloseIcon from "@mui/icons-material/Close";

import { useState } from "react";

const HotelCard = ({ hotel }) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const handleImageClick = () => {
    setIsImageModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsImageModalOpen(false);
  };

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: 896,
        mx: "auto",
        boxShadow: 3,
        "&:hover": { boxShadow: 6 },
        transition: "box-shadow 0.3s",
        border: 2,
        borderColor: "primary.light",
      }}
    >
      <CardHeader
        sx={{
          background: "linear-gradient(to right, #1a365d, #1e4a82)",
          color: "white",
          py: 3,
          px: 4,
        }}
        title={
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                minWidth: 0,
                flex: 1,
              }}
            >
              {hotel.logo && (
                <Box
                  sx={{
                    width: { xs: 70, md: 90 },
                    height: { xs: 70, md: 90 },
                    bgcolor: "white",
                    borderRadius: 1,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={hotel.logo}
                    alt={hotel.name}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "1rem", md: "1.8rem" }, // Responsive font size
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.2,
                  }}
                >
                  {hotel.name}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                textAlign: { md: "right" },
                flexShrink: 0,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "semibold" }}>
                HOSPEDAJE
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {hotel.dates}
              </Typography>
            </Box>
          </Box>
        }
      />

      <CardContent sx={{ p: 6, bgcolor: "#0c1429" }}>
        <Grid container spacing={4}>
          {/* Left Column - All Hotel Information */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Room Prices Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 4, color: "white", textAlign: "center" }}
                >
                  Precios de Habitaciones
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {hotel.rooms.map((room, index) => (
                    <Box
                      key={index}
                      sx={{
                        bgcolor: "#142141",
                        p: 4,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "primary.light",
                        "&:hover": { boxShadow: 3 },
                        transition: "all 0.3s",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            flex: "1 1 60%",
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            fontWeight="medium"
                            color="white"
                            noWrap={false}
                            sx={{
                              wordBreak: "break-word",
                            }}
                          >
                            {room.name}
                          </Typography>
                          <Typography variant="body2" color="white">
                            {room.taxes}
                          </Typography>
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "white",
                            flexShrink: 0, // Prevents price from shrinking
                            whiteSpace: "nowrap", // Keeps price on one line
                            ml: 1,
                          }}
                        >
                          ${room.price}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                {/* Amenities */}
                <Box
                  sx={{
                    mt: 4,
                    p: 4,
                    bgcolor: "blue.50",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      INCLUYE:
                    </Typography>
                    <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {hotel.amenities.map((amenity, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexShrink: 0, // Prevent shrinking
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: { xs: "70px", sm: "none" },
                            }}
                          >
                            {amenity}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ borderColor: "primary.light" }} />

              {/* Location */}
              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <MapPinIcon sx={{ color: "primary.light", fontSize: 20 }} />
                  <Typography variant="h6" sx={{ color: "primary.light" }}>
                    Ubicación
                  </Typography>
                </Box>
                <Typography color="white">{hotel.location.address}</Typography>
                <Typography color="white">
                  {hotel.location.zipCode} | {hotel.location.city},{" "}
                  {hotel.location.state}
                </Typography>
              </Box>

              <Divider sx={{ borderColor: "primary.light" }} />

              {/* Contact Info */}
              <Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <PhoneIcon sx={{ color: "primary.light", fontSize: 20 }} />
                  <Typography variant="h6" sx={{ color: "primary.light" }}>
                    Contacto
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <PhoneIcon fontSize="small" sx={{ color: "white" }} />
                    <Typography color="white">
                      Tel. {hotel.contact.phone}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <MailIcon fontSize="small" sx={{ color: "white" }} />
                    <Typography
                      color="white"
                      sx={{
                        wordBreak: "break-all", // Breaks long email addresses
                        overflowWrap: "break-word", // Alternative to word-break
                        flex: 1, // Takes available space
                        minWidth: 0, // Allows text wrapping
                      }}
                    >
                      Email:{" "}
                      <Box
                        component="span"
                        sx={{
                          color: "orange.600",
                          fontWeight: "medium",
                          display: "inline-block",
                          maxWidth: "100%",
                        }}
                      >
                        {hotel.contact.email}
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ borderColor: "primary.light" }} />

              {/* Hours */}
              {hotel.hours && (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <ClockIcon sx={{ color: "primary.light", fontSize: 20 }} />
                    <Typography variant="h6" sx={{ color: "primary.light" }}>
                      Horarios
                    </Typography>
                  </Box>
                  {hotel.hours.weekdays && (
                    <Typography color="white">
                      Lunes a Viernes: {hotel.hours.weekdays}
                    </Typography>
                  )}
                  {hotel.hours.weekend && (
                    <Typography color="white">
                      Sábado: {hotel.hours.weekend}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Right Column - Hotel Poster/Image */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  bgcolor: "grey.100",
                  borderRadius: 2,
                  overflow: "hidden",
                  width: "100%",
                  height: 500,
                  paddingBottom: "100%",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: 3,
                    transform: "scale(1.02)",
                    transition: "transform 0.3s",
                  },
                }}
                onClick={handleImageClick}
              >
                <img
                  src={hotel.image || "https://via.placeholder.com/400"}
                  alt={`${hotel.name} - Vista`}
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Image Modal */}
          <Modal
            open={isImageModalOpen}
            onClose={handleCloseModal}
            aria-labelledby="image-modal-title"
            aria-describedby="image-modal-description"
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "rgba(0,0,0,0.9)",
                overflow: "auto",
                p: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <IconButton
                aria-label="close"
                onClick={handleCloseModal}
                sx={{
                  position: "fixed",
                  right: 16,
                  top: 16,
                  color: "common.white",
                  bgcolor: "rgba(0,0,0,0.5)",
                  zIndex: 1,
                  "&:hover": {
                    bgcolor: "rgba(0,0,0,0.7)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box
                sx={{
                  my: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <img
                  src={hotel.image || "https://via.placeholder.com/400"}
                  alt={`${hotel.name} - Vista Ampliada`}
                  style={{
                    width: "auto",
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: "none",
                  }}
                />
              </Box>
            </Box>
          </Modal>
        </Grid>

        <Box
          sx={{
            mt: 6,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
            width: "100%",
            px: 2,
          }}
        >
          <Divider sx={{ width: "100%" }} />
          <Box
            sx={{
              bgcolor: "#142141",
              p: { xs: 3, sm: 4 },
              borderRadius: 2,
              width: "100%",
              maxWidth: 600,
              textAlign: "center",
              boxSizing: "border-box",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.5, sm: 1 },
              }}
            >
              {hotel.reservationCode ? (
                <>
                  Genera tu reservación mencionando tu código:
                  <Box
                    component="span"
                    sx={{
                      fontWeight: "bold",
                      color: "primary.light",
                      wordBreak: "break-word",
                      display: "inline-block",
                      maxWidth: "100%",
                    }}
                  >
                    {hotel.reservationCode}
                  </Box>
                </>
              ) : (
                "Menciona que vienes al evento de coheteria en la Laguna de Sayula"
              )}
            </Typography>
          </Box>

          {/* Only show reservation button if reservationUrl exists */}
          {hotel.reservationUrl && (
            <Button
              variant="contained"
              sx={{
                bgcolor: "purple.600",
                "&:hover": { bgcolor: "purple.700" },
                alignSelf: "center",
                width: { xs: "100%", sm: "auto" },
                maxWidth: 300,
              }}
              onClick={() => window.open(hotel.reservationUrl, "_blank")}
            >
              Hacer Reservación
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default HotelCard;
